import { Box, Flex, Portal, Text } from '@chakra-ui/react';
import mapboxgl, { type ExpressionSpecification } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import { useEffect, useRef, useState } from 'react';

import {
  MAPBOX_STYLE_URL,
  MAPBOX_TOKEN,
} from '../../CustomDash/components/Chart/Map/mapbox-config';
import { geoFromFips } from '../data/us-geo';
import statesGeoJsonRaw from '../data/us-states.geojson.json';
import { fmtCount, fmtUSD } from '../format';
import type { SelectedGeo } from '../types';
import { DII_COLOR, DII_LABEL, DISPOSITIONS } from './StackedDiiBars';

const STATES_SOURCE = 'econ-impact-states';
const STATES_FILL_LAYER = 'econ-impact-states-fill';
const STATES_SELECTED_LAYER = 'econ-impact-states-fill-selected';
const STATES_LINE_LAYER = 'econ-impact-states-line';

const COUNTIES_SOURCE = 'econ-impact-counties';
const COUNTIES_FILL_LAYER = 'econ-impact-counties-fill';
const COUNTIES_SELECTED_LAYER = 'econ-impact-counties-fill-selected';
const COUNTIES_HIGHLIGHT_LAYER = 'econ-impact-counties-highlight';
const COUNTIES_LINE_LAYER = 'econ-impact-counties-line';

const COLOR_FILL = '#46bef1';
const COLOR_HIGHLIGHT = '#3dc679'; // green.500 — hovered/selected fill
const COLOR_OUTLINE = '#2D426A';
const COLOR_HEAT_HIGHLIGHT = '#237647'; // green.700

// Explore/select fill: idle blue, green when hovered OR selected, so both the
// hovered and the picked output state/county read in green. Shared across the
// state + county fill layers and the heat-mode exit reset. (In heat mode the
// fills are repainted with the heat ramp, so the `selected` state only tints
// the fill on the first/explore page.)
const HOVER_OR_SELECTED = [
  'any',
  ['boolean', ['feature-state', 'hover'], false],
  ['boolean', ['feature-state', 'selected'], false],
];
const SELECT_FILL_COLOR = [
  'case',
  HOVER_OR_SELECTED,
  COLOR_HIGHLIGHT,
  COLOR_FILL,
] as unknown as ExpressionSpecification;
const COUNTY_FILL_OPACITY = [
  'case',
  HOVER_OR_SELECTED,
  0.7,
  0.08,
] as unknown as ExpressionSpecification;
const STATE_FILL_OPACITY = [
  'case',
  HOVER_OR_SELECTED,
  0.55,
  0.15,
] as unknown as ExpressionSpecification;

// County boundaries fade in once the user is zoomed into a state-ish
// viewport; clicks select counties instead of states from there.
const COUNTY_VISIBLE_ZOOM = 4.5;

// Default frame: the mid-Atlantic east coast, tight enough that the fitted
// zoom clears COUNTY_VISIBLE_ZOOM so county boundaries are visible on first
// paint. The rest of the country stays reachable via the geography tree (and
// by panning/zooming out).
const EAST_COAST_BOUNDS: [[number, number], [number, number]] = [
  [-82, 34.5],
  [-73, 41.5],
];

interface GeoFeatureProps {
  fips: string;
  name: string;
}
type AnyPolygon = GeoJSON.Polygon | GeoJSON.MultiPolygon;
type GeoCollection = GeoJSON.FeatureCollection<AnyPolygon, GeoFeatureProps>;

// Bundled census-derived state geometry (provenance in data/us-states.ts).
// Feature ids are numeric FIPS, so mapbox feature-state (hover) works on
// a geojson source without promoteId. County geometry (~2.3MB) is code-
// split and only loaded once the user zooms in, picks a geography, or a
// results heat map is requested.
const usStatesGeoJson = statesGeoJsonRaw as unknown as GeoCollection;

type Bbox = [number, number, number, number];

function expandBbox(acc: Bbox, lng: number, lat: number) {
  if (lng < acc[0]) acc[0] = lng;
  if (lat < acc[1]) acc[1] = lat;
  if (lng > acc[2]) acc[2] = lng;
  if (lat > acc[3]) acc[3] = lat;
}

function accFeatureBbox(feature: GeoJSON.Feature<AnyPolygon>, acc: Bbox): Bbox {
  const rings =
    feature.geometry.type === 'Polygon'
      ? feature.geometry.coordinates
      : feature.geometry.coordinates.flat();
  for (const ring of rings) {
    for (const pos of ring) {
      const [lng, lat] = pos;
      if (typeof lng === 'number' && typeof lat === 'number') {
        expandBbox(acc, lng, lat);
      }
    }
  }
  return acc;
}

const bboxOfFeature = (feature: GeoJSON.Feature<AnyPolygon>): Bbox =>
  accFeatureBbox(feature, [Infinity, Infinity, -Infinity, -Infinity]);

const isFiniteBbox = (b: Bbox) => b.every((n) => Number.isFinite(n));

const bboxToLngLat = (b: Bbox): [[number, number], [number, number]] => [
  [b[0], b[1]],
  [b[2], b[3]],
];

const findFeature = (collection: GeoCollection, fips: string) =>
  collection.features.find((feature) => feature.properties.fips === fips);

// Light → dark green ramp for the results county heat (t in [0,1]).
function greenShade(t: number): string {
  const from = [0xdc, 0xf3, 0xe6];
  const to = [0x2f, 0xa2, 0x61];
  const ch = (i: number) =>
    Math.round(
      (from[i] as number) + ((to[i] as number) - (from[i] as number)) * t
    );
  return `rgb(${ch(0)}, ${ch(1)}, ${ch(2)})`;
}

export interface CountyIndustryDatum {
  label: string;
  direct: number;
  indirect: number;
  induced: number;
}
export interface CountyHeatDatum {
  name: string;
  direct: number;
  indirect: number;
  induced: number;
  topIndustries: CountyIndustryDatum[];
}

const datumTotal = (d: { direct: number; indirect: number; induced: number }) =>
  d.direct + d.indirect + d.induced;

const TOOLTIP_WIDTH = 272;
const TOOLTIP_EST_HEIGHT = 330;

// Design's county hover card: name + total, the D/I/I split, then the
// county's top industries each as a mini direct/indirect/induced bar.
// Rendered in a Portal at the cursor's viewport position (with edge flip)
// so it's never clipped by the map card.
function MapCountyTooltip({
  datum,
  clientX,
  clientY,
}: {
  datum: CountyHeatDatum;
  clientX: number;
  clientY: number;
}) {
  const total = datumTotal(datum);
  // Size the industry bars by their share of the shown industries' combined
  // impact (not the single largest), so they read as relative to each other
  // without the top one always pinning to full width.
  const industriesTotal = Math.max(
    1,
    datum.topIndustries.reduce((sum, i) => sum + datumTotal(i), 0)
  );
  const flipX = clientX + TOOLTIP_WIDTH + 14 > window.innerWidth;
  const flipY = clientY + TOOLTIP_EST_HEIGHT + 14 > window.innerHeight;
  const left = flipX ? clientX - TOOLTIP_WIDTH - 14 : clientX + 14;
  const top = flipY
    ? Math.max(8, clientY - TOOLTIP_EST_HEIGHT - 14)
    : clientY + 14;
  return (
    <Box
      position="fixed"
      left={`${left}px`}
      top={`${top}px`}
      zIndex={1600}
      pointerEvents="none"
      w={`${TOOLTIP_WIDTH}px`}
      bg="navyBlue.500"
      color="white"
      borderRadius="10px"
      boxShadow="0 16px 36px rgba(20,30,55,.32)"
      p="13px 15px"
      data-testid="map-county-tooltip"
    >
      <Flex align="baseline" justify="space-between" gap="8px">
        <Text color="white" fontWeight="700" fontSize="14px" noOfLines={1}>
          {datum.name}
        </Text>
        <Text color="white" fontFamily="mono" fontWeight="700" fontSize="14px">
          {fmtUSD(total)}
        </Text>
      </Flex>
      <Box h="1px" bg="whiteAlpha.300" my="10px" />
      <Text
        fontSize="9.5px"
        fontWeight="600"
        color="whiteAlpha.700"
        textTransform="uppercase"
        letterSpacing="0.6px"
        mb="7px"
      >
        Direct / Indirect / Induced
      </Text>
      <Flex h="11px" borderRadius="3px" overflow="hidden" gap="1.5px">
        {DISPOSITIONS.map((d) => (
          <Box
            key={d}
            width={`${total > 0 ? (datum[d] / total) * 100 : 0}%`}
            bg={DII_COLOR[d]}
          />
        ))}
      </Flex>
      <Flex direction="column" gap="5px" mt="9px">
        {DISPOSITIONS.map((d) => (
          <Flex key={d} align="center" gap="7px">
            <Box
              w="8px"
              h="8px"
              borderRadius="2px"
              bg={DII_COLOR[d]}
              flexShrink={0}
            />
            <Text fontSize="11px" color="whiteAlpha.800">
              {DII_LABEL[d]}
            </Text>
            <Box flex="1" />
            <Text
              fontFamily="mono"
              fontSize="11px"
              fontWeight="600"
              color="white"
            >
              {fmtUSD(datum[d])}
            </Text>
          </Flex>
        ))}
      </Flex>
      {datum.topIndustries.length > 0 && (
        <>
          <Box h="1px" bg="whiteAlpha.300" my="10px" />
          <Text
            fontSize="9.5px"
            fontWeight="600"
            color="whiteAlpha.700"
            textTransform="uppercase"
            letterSpacing="0.6px"
            mb="7px"
          >
            Top industries impacted
          </Text>
          <Flex direction="column" gap="6px">
            {datum.topIndustries.map((industry) => {
              const indTotal = datumTotal(industry);
              return (
                <Flex key={industry.label} align="center" gap="8px">
                  <Text
                    w="92px"
                    flexShrink={0}
                    fontSize="11px"
                    color="whiteAlpha.900"
                    noOfLines={1}
                  >
                    {industry.label}
                  </Text>
                  <Box flex="1" h="7px">
                    <Flex
                      h="100%"
                      width={`${(indTotal / industriesTotal) * 100}%`}
                      gap="1px"
                      borderRadius="2px"
                      overflow="hidden"
                    >
                      {DISPOSITIONS.map((d) => (
                        <Box
                          key={d}
                          width={`${indTotal > 0 ? (industry[d] / indTotal) * 100 : 0}%`}
                          bg={DII_COLOR[d]}
                        />
                      ))}
                    </Flex>
                  </Box>
                  <Text
                    w="46px"
                    flexShrink={0}
                    textAlign="right"
                    fontFamily="mono"
                    fontSize="10.5px"
                    fontWeight="600"
                    color="whiteAlpha.900"
                  >
                    {fmtUSD(indTotal)}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        </>
      )}
    </Box>
  );
}

// Explore-mode hover card figures. Real impact numbers only exist once a
// scenario runs, so before then we surface stable, plausible stats seeded by
// FIPS — the same geography yields the same numbers on every hover.
interface GeoStats {
  employment: number;
  output: number;
  valueAdded: number;
}

function fipsSeed(fips: string): number {
  let h = 2166136261;
  for (let i = 0; i < fips.length; i++) {
    h = Math.imul(h ^ fips.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

function geoStatsFor(fips: string): GeoStats {
  const seed = fipsSeed(fips);
  // Deterministic [0,1) draws keyed off the seed (no Math.random — stable).
  const unit = (n: number) => {
    const x = Math.sin(seed * 0.013 + n * 2.399) * 43758.5453;
    return x - Math.floor(x);
  };
  const employment = Math.round((0.4 + unit(1) * 7.6) * 1e6); // ~0.4M–8M
  const output = (80 + unit(2) * 1320) * 1e9; // ~$80B–$1.4T
  const valueAdded = output * (0.45 + unit(3) * 0.25); // 45–70% of output
  return { employment, output, valueAdded };
}

const EXPLORE_TOOLTIP_WIDTH = 203;
const EXPLORE_TOOLTIP_EST_HEIGHT = 94;

// Explore-mode hover card (no scenario yet): "<name> - <fips>" header over
// the geography's headline economic stats. Anchored beside the cursor with
// the caret pointing at it — the card is vertically centered on the cursor
// and the caret tracks the cursor's exact Y, so the pointer stays on the
// hovered geography instead of drifting south.
function MapExploreTooltip({
  name,
  fips,
  clientX,
  clientY,
}: {
  name: string;
  fips: string;
  clientX: number;
  clientY: number;
}) {
  const stats = geoStatsFor(fips);
  // Card-edge-to-cursor gap (the caret pokes ~9px of this back toward the
  // cursor). Flip to the cursor's left when there isn't room on the right.
  const GAP = 16;
  const flipX = clientX + GAP + EXPLORE_TOOLTIP_WIDTH + 10 > window.innerWidth;
  const left = flipX ? clientX - EXPLORE_TOOLTIP_WIDTH - GAP : clientX + GAP;
  const top = Math.min(
    Math.max(clientY - EXPLORE_TOOLTIP_EST_HEIGHT / 2, 8),
    window.innerHeight - EXPLORE_TOOLTIP_EST_HEIGHT - 8
  );
  // Caret centre relative to the card top — follows the cursor even when the
  // card is clamped against a viewport edge.
  const caretTop = Math.min(
    Math.max(clientY - top, 12),
    EXPLORE_TOOLTIP_EST_HEIGHT - 12
  );
  const rows: Array<[string, string]> = [
    ['Employment', fmtCount(stats.employment)],
    ['Output', fmtUSD(stats.output)],
    ['Value Added', fmtUSD(stats.valueAdded)],
  ];
  return (
    <Box
      position="fixed"
      left={`${left}px`}
      top={`${top}px`}
      zIndex={1600}
      pointerEvents="none"
      w={`${EXPLORE_TOOLTIP_WIDTH}px`}
    >
      {/* Caret: a rotated square that pokes ~8px out of the card edge facing
          the cursor (left by default, right when the card is flipped). Same
          fill as the body so it reads as a pointer (Figma node 51:3379). */}
      <Box
        position="absolute"
        top={`${caretTop}px`}
        {...(flipX ? { right: '-6px' } : { left: '-6px' })}
        w="14px"
        h="14px"
        bg="navyBlue.500"
        transform="translateY(-50%) rotate(45deg)"
        borderRadius="2px"
      />
      <Box
        position="relative"
        w="100%"
        bg="navyBlue.500"
        borderRadius="8px"
        boxShadow="0 16px 36px rgba(20,30,55,.32)"
        overflow="hidden"
        data-testid="map-explore-tooltip"
      >
        <Box
          bg="navyBlue.600"
          h="29px"
          px="13px"
          display="flex"
          alignItems="center"
        >
          <Text
            color="white"
            fontWeight="600"
            fontSize="12px"
            lineHeight="1.2"
            noOfLines={1}
          >
            {name} - {fips}
          </Text>
        </Box>
        <Flex direction="column" gap="2px" px="13px" pt="9px" pb="13px">
          {rows.map(([label, value]) => (
            <Flex key={label} align="center" justify="space-between" gap="12px">
              <Text fontSize="10px" lineHeight="1.2" color="white">
                {label}
              </Text>
              <Text
                fontSize="10px"
                lineHeight="1.2"
                color="white"
                textAlign="right"
              >
                {value}
              </Text>
            </Flex>
          ))}
        </Flex>
      </Box>
    </Box>
  );
}

// Minimal hover label for the heat map: just the geography's name, shown for
// states (zoomed out) and for counties that aren't in the impact data — so
// there's always feedback on what you're hovering over.
function MapNameTooltip({
  fips,
  name,
  clientX,
  clientY,
}: {
  fips: string;
  name: string;
  clientX: number;
  clientY: number;
}) {
  const display = geoFromFips(fips)?.name ?? name;
  const flipX = clientX + 220 + 14 > window.innerWidth;
  const left = flipX ? clientX - 14 : clientX + 14;
  return (
    <Box
      position="fixed"
      left={`${left}px`}
      top={`${clientY + 16}px`}
      transform={flipX ? 'translateX(-100%)' : undefined}
      zIndex={1600}
      pointerEvents="none"
      bg="navyBlue.500"
      borderRadius="8px"
      boxShadow="0 8px 22px rgba(20,30,55,.28)"
      px="12px"
      py="7px"
      maxW="240px"
      data-testid="map-name-tooltip"
    >
      <Text fontSize="12.5px" fontWeight="600" color="white" noOfLines={1}>
        {display}
      </Text>
    </Box>
  );
}

interface EconomicImpactMapProps {
  selectedGeo: SelectedGeo | null;
  onSelectGeo: (geo: SelectedGeo) => void;
  /**
   * Results "heat" mode: county fips → per-county breakdown. When set, the
   * map loads county geometry, green-shades these counties by total impact,
   * fits to them, and shows a hover tooltip. Absent → explore mode
   * (state/county selection).
   */
  counties?: ReadonlyMap<string, CountyHeatDatum>;
  /** County fips to emphasize (cross-highlight from the counties table). */
  highlightedFips?: readonly string[];
  /** Fired as the cursor enters/leaves a county in heat mode. */
  onHoverCounty?: (fips: string | null) => void;
  /**
   * Frame the camera on `selectedGeo` even in heat mode (the heat still
   * renders). Variant 4 uses this so the map tracks the output geography
   * rather than zooming to wherever the scenarios' impact lands.
   */
  fitToSelected?: boolean;
  /**
   * Green-fill the selected geo (the "selection" step before results). The
   * output/results map turns this off so it shows only the impact heat plus
   * the selected-geo outline.
   */
  highlightSelection?: boolean;
  /**
   * Whether clicking the map selects a geography. The output/results map turns
   * this off — there the geography is changed only via the title dropdown.
   */
  selectable?: boolean;
}

export function EconomicImpactMap({
  selectedGeo,
  onSelectGeo,
  counties,
  highlightedFips,
  onHoverCounty,
  fitToSelected = false,
  highlightSelection = true,
  selectable = true,
}: EconomicImpactMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const styleLoadedRef = useRef(false);
  const hoveredStateIdRef = useRef<string | number | null>(null);
  const hoveredCountyIdRef = useRef<string | number | null>(null);
  // The feature currently flagged with `selected` feature-state (green fill on
  // the explore page), so we can clear it when the selection changes.
  const selectedFeatureRef = useRef<{ source: string; id: number } | null>(
    null
  );
  const countiesGeoJsonRef = useRef<GeoCollection | null>(null);
  const countiesRequestedRef = useRef(false);
  // Signature of what the camera was last fitted to, so we don't re-fit
  // (yanking the user's pan/zoom) when the effect re-runs with the same
  // selection/impacted-set — e.g. when the lazy county chunk lands.
  const lastFitSignatureRef = useRef<string | null>(null);
  const [countiesReady, setCountiesReady] = useState(false);
  const [initError, setInitError] = useState(false);
  const [tooltip, setTooltip] = useState<{
    fips: string;
    name: string;
    clientX: number;
    clientY: number;
  } | null>(null);

  const heatMode = counties !== undefined && counties.size > 0;
  // No scenario yet (the page renders the map without a `counties` prop):
  // hovering any state/county shows the per-geography explore card.
  const exploreMode = counties === undefined;

  // Latest values in refs so map handlers stay bound for the life of the
  // map without re-attaching when props change.
  const onSelectGeoRef = useRef(onSelectGeo);
  onSelectGeoRef.current = onSelectGeo;
  const onHoverCountyRef = useRef(onHoverCounty);
  onHoverCountyRef.current = onHoverCounty;
  const heatModeRef = useRef(heatMode);
  heatModeRef.current = heatMode;
  const fitToSelectedRef = useRef(fitToSelected);
  fitToSelectedRef.current = fitToSelected;
  const highlightSelectionRef = useRef(highlightSelection);
  highlightSelectionRef.current = highlightSelection;
  const selectableRef = useRef(selectable);
  selectableRef.current = selectable;
  const exploreModeRef = useRef(exploreMode);
  exploreModeRef.current = exploreMode;

  // Counties "own" clicks/hover only once the camera is zoomed into a
  // state-ish viewport. This is purely zoom-based (even in heat mode) so that
  // zooming out on the results map lets you click a whole state, not just a
  // county.
  const countiesActive = (map: mapboxgl.Map) =>
    countiesGeoJsonRef.current !== null && map.getZoom() >= COUNTY_VISIBLE_ZOOM;

  // Code-split county geometry; layers attach once both the style and
  // the chunk are ready.
  const ensureCounties = () => {
    if (countiesRequestedRef.current) return;
    countiesRequestedRef.current = true;
    import('../data/us-counties.geojson.json').then((module) => {
      countiesGeoJsonRef.current = module.default as unknown as GeoCollection;
      addCountyLayers();
      setCountiesReady(true);
    });
  };

  const addCountyLayers = () => {
    const map = mapRef.current;
    const counties = countiesGeoJsonRef.current;
    if (!map || !counties || !styleLoadedRef.current) return;
    if (map.getSource(COUNTIES_SOURCE)) return;

    map.addSource(COUNTIES_SOURCE, { type: 'geojson', data: counties });

    map.addLayer({
      id: COUNTIES_FILL_LAYER,
      type: 'fill',
      source: COUNTIES_SOURCE,
      minzoom: COUNTY_VISIBLE_ZOOM,
      paint: {
        'fill-color': SELECT_FILL_COLOR,
        'fill-opacity': COUNTY_FILL_OPACITY,
      },
    });

    map.addLayer({
      id: COUNTIES_SELECTED_LAYER,
      type: 'line',
      source: COUNTIES_SOURCE,
      filter: ['==', ['get', 'fips'], '__none__'],
      paint: { 'line-color': COLOR_HIGHLIGHT, 'line-width': 3 },
    });

    map.addLayer({
      id: COUNTIES_HIGHLIGHT_LAYER,
      type: 'line',
      source: COUNTIES_SOURCE,
      filter: ['==', ['get', 'fips'], '__none__'],
      paint: { 'line-color': COLOR_HEAT_HIGHLIGHT, 'line-width': 2.5 },
    });

    map.addLayer({
      id: COUNTIES_LINE_LAYER,
      type: 'line',
      source: COUNTIES_SOURCE,
      minzoom: COUNTY_VISIBLE_ZOOM,
      paint: {
        'line-color': COLOR_OUTLINE,
        'line-width': 0.5,
        'line-opacity': 0.5,
      },
    });

    map.on('click', COUNTIES_FILL_LAYER, (e) => {
      if (!selectableRef.current) return;
      // Zoomed out (counties not active), the state layer owns the click —
      // even though the heat counties still render, a click selects the state.
      if (!countiesActive(map)) return;
      const props = e.features?.[0]?.properties as
        | Partial<GeoFeatureProps>
        | undefined;
      if (!props?.fips) return;
      const geo = geoFromFips(props.fips);
      if (geo) onSelectGeoRef.current(geo);
    });

    map.on('mousemove', COUNTIES_FILL_LAYER, (e) => {
      map.getCanvas().style.cursor = 'pointer';
      const feature = e.features?.[0];
      const featureId = feature?.id ?? null;
      const props = feature?.properties as Partial<GeoFeatureProps> | undefined;
      const fips = props?.fips;
      if (featureId === null) return;
      if (
        hoveredCountyIdRef.current !== null &&
        hoveredCountyIdRef.current !== featureId
      ) {
        map.setFeatureState(
          { source: COUNTIES_SOURCE, id: hoveredCountyIdRef.current },
          { hover: false }
        );
      }
      hoveredCountyIdRef.current = featureId;
      map.setFeatureState(
        { source: COUNTIES_SOURCE, id: featureId },
        { hover: true }
      );
      if (fips) {
        onHoverCountyRef.current?.(fips);
        // Heat mode reads its datum from `counties`; explore mode reads the
        // seeded stats off the fips. Either way the card needs name + fips.
        if (heatModeRef.current || exploreModeRef.current) {
          setTooltip({
            fips,
            name: props?.name ?? '',
            clientX: e.originalEvent.clientX,
            clientY: e.originalEvent.clientY,
          });
        }
      }
    });

    map.on('mouseleave', COUNTIES_FILL_LAYER, () => {
      if (hoveredCountyIdRef.current !== null) {
        map.setFeatureState(
          { source: COUNTIES_SOURCE, id: hoveredCountyIdRef.current },
          { hover: false }
        );
        hoveredCountyIdRef.current = null;
      }
      onHoverCountyRef.current?.(null);
      setTooltip(null);
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: map mounts once; handlers read latest values through refs
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;

    let map: mapboxgl.Map;
    try {
      map = new mapboxgl.Map({
        container: containerRef.current,
        style: MAPBOX_STYLE_URL,
        bounds: EAST_COAST_BOUNDS,
        fitBoundsOptions: { padding: 24 },
        projection: 'mercator',
        renderWorldCopies: false,
        dragRotate: false,
        touchPitch: false,
        attributionControl: false,
      });
    } catch {
      // Mapbox throws synchronously when WebGL is unavailable.
      setInitError(true);
      return;
    }
    mapRef.current = map;

    map.on('style.load', () => {
      map.addSource(STATES_SOURCE, { type: 'geojson', data: usStatesGeoJson });

      map.addLayer({
        id: STATES_FILL_LAYER,
        type: 'fill',
        source: STATES_SOURCE,
        paint: {
          'fill-color': SELECT_FILL_COLOR,
          'fill-opacity': STATE_FILL_OPACITY,
        },
      });

      map.addLayer({
        id: STATES_SELECTED_LAYER,
        type: 'line',
        source: STATES_SOURCE,
        filter: ['==', ['get', 'fips'], '__none__'],
        paint: { 'line-color': COLOR_HIGHLIGHT, 'line-width': 3 },
      });

      map.addLayer({
        id: STATES_LINE_LAYER,
        type: 'line',
        source: STATES_SOURCE,
        paint: { 'line-color': COLOR_OUTLINE, 'line-width': 1 },
      });

      map.on('click', STATES_FILL_LAYER, (e) => {
        if (!selectableRef.current) return;
        // Counties own clicks once they're visible (zoomed in / heat mode).
        if (countiesActive(map)) return;
        const props = e.features?.[0]?.properties as
          | Partial<GeoFeatureProps>
          | undefined;
        if (!props?.fips) return;
        const geo = geoFromFips(props.fips);
        if (geo) onSelectGeoRef.current(geo);
      });

      map.on('mousemove', STATES_FILL_LAYER, (e) => {
        // Counties own hover/selection once zoomed in; below that (including
        // the zoomed-out state-heat view) hovering a state shows the pointer
        // so it reads as selectable. In heat mode the state fill is painted
        // with the heat colour, so the hover feature-state has no visual
        // effect there — only the cursor changes.
        if (countiesActive(map)) {
          if (hoveredStateIdRef.current !== null) {
            map.setFeatureState(
              { source: STATES_SOURCE, id: hoveredStateIdRef.current },
              { hover: false }
            );
            hoveredStateIdRef.current = null;
          }
          return;
        }
        map.getCanvas().style.cursor = 'pointer';
        const feature = e.features?.[0];
        const featureId = feature?.id ?? null;
        if (featureId === null) return;
        if (
          hoveredStateIdRef.current !== null &&
          hoveredStateIdRef.current !== featureId
        ) {
          map.setFeatureState(
            { source: STATES_SOURCE, id: hoveredStateIdRef.current },
            { hover: false }
          );
        }
        hoveredStateIdRef.current = featureId;
        map.setFeatureState(
          { source: STATES_SOURCE, id: featureId },
          { hover: true }
        );
        // Explore mode shows the full stat card; heat mode (zoomed out) shows
        // at least the state name so the user knows what they're hovering.
        if (exploreModeRef.current || heatModeRef.current) {
          const props = feature?.properties as
            | Partial<GeoFeatureProps>
            | undefined;
          if (props?.fips) {
            setTooltip({
              fips: props.fips,
              name: props.name ?? '',
              clientX: e.originalEvent.clientX,
              clientY: e.originalEvent.clientY,
            });
          }
        }
      });

      map.on('mouseleave', STATES_FILL_LAYER, () => {
        map.getCanvas().style.cursor = '';
        if (hoveredStateIdRef.current !== null) {
          map.setFeatureState(
            { source: STATES_SOURCE, id: hoveredStateIdRef.current },
            { hover: false }
          );
          hoveredStateIdRef.current = null;
        }
        setTooltip(null);
      });

      styleLoadedRef.current = true;
      addCountyLayers();
    });

    map.on('zoomend', () => {
      if (map.getZoom() >= COUNTY_VISIBLE_ZOOM - 1) ensureCounties();
    });

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(() => {
        if (mapRef.current?.getContainer()) mapRef.current.resize();
      }, 0);
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      mapRef.current?.remove();
      mapRef.current = null;
      styleLoadedRef.current = false;
      hoveredStateIdRef.current = null;
      hoveredCountyIdRef.current = null;
    };
  }, []);

  // Heat mode: load counties immediately and green-shade them by value.
  // biome-ignore lint/correctness/useExhaustiveDependencies: countiesReady re-runs once the lazy chunk lands; map read via ref
  useEffect(() => {
    if (heatMode) ensureCounties();
    const map = mapRef.current;
    if (!map) return;

    const heatColorRamp = (value: number, max: number) =>
      greenShade((value / max) ** 0.45);

    const apply = () => {
      if (!map.getLayer(COUNTIES_FILL_LAYER)) return;
      if (heatMode && counties) {
        // County-level heat — shown only once zoomed past the county
        // threshold; below it, the state-level heat (below) takes over.
        const totals = [...counties].map(
          ([fips, d]) => [fips, datumTotal(d)] as const
        );
        const max = Math.max(...totals.map(([, v]) => v), 1);
        const match: unknown[] = ['match', ['get', 'fips']];
        for (const [fips, value] of totals) {
          match.push(fips, heatColorRamp(value, max));
        }
        match.push('#e2e8f0'); // gray.200 fallback for untouched counties
        map.setPaintProperty(
          COUNTIES_FILL_LAYER,
          'fill-color',
          match as unknown as ExpressionSpecification
        );
        map.setPaintProperty(COUNTIES_FILL_LAYER, 'fill-opacity', [
          'case',
          ['in', ['get', 'fips'], ['literal', [...counties.keys()]]],
          0.85,
          0.05,
        ]);
        map.setLayerZoomRange(COUNTIES_FILL_LAYER, COUNTY_VISIBLE_ZOOM, 24);
        map.setLayerZoomRange(COUNTIES_LINE_LAYER, COUNTY_VISIBLE_ZOOM, 24);

        // State-level heat: aggregate the county totals up to their state so
        // zooming out reads as a state choropleth you can click to select a
        // whole state. (Counties fade out below COUNTY_VISIBLE_ZOOM; above it
        // the opaque county heat sits on top of this.)
        const stateTotals = new Map<string, number>();
        for (const [fips, value] of totals) {
          const stateFips = fips.slice(0, 2);
          stateTotals.set(stateFips, (stateTotals.get(stateFips) ?? 0) + value);
        }
        const stateMax = Math.max(...stateTotals.values(), 1);
        const stateMatch: unknown[] = ['match', ['get', 'fips']];
        for (const [stateFips, value] of stateTotals) {
          stateMatch.push(stateFips, heatColorRamp(value, stateMax));
        }
        stateMatch.push('#e2e8f0');
        map.setPaintProperty(
          STATES_FILL_LAYER,
          'fill-color',
          stateMatch as unknown as ExpressionSpecification
        );
        map.setPaintProperty(STATES_FILL_LAYER, 'fill-opacity', [
          'case',
          ['in', ['get', 'fips'], ['literal', [...stateTotals.keys()]]],
          0.85,
          0.05,
        ]);
        // State heat only below the county threshold — above it the county
        // heat takes over. Without this cap the state fill bleeds through the
        // faint (0.05) non-impacted counties, greening the whole state.
        map.setLayerZoomRange(STATES_FILL_LAYER, 0, COUNTY_VISIBLE_ZOOM);
      } else {
        map.setPaintProperty(
          COUNTIES_FILL_LAYER,
          'fill-color',
          SELECT_FILL_COLOR
        );
        map.setPaintProperty(
          COUNTIES_FILL_LAYER,
          'fill-opacity',
          COUNTY_FILL_OPACITY
        );
        map.setLayerZoomRange(COUNTIES_FILL_LAYER, COUNTY_VISIBLE_ZOOM, 24);
        map.setLayerZoomRange(COUNTIES_LINE_LAYER, COUNTY_VISIBLE_ZOOM, 24);
        // Restore the explore-mode state fill (idle blue / green hover) at all
        // zooms.
        map.setPaintProperty(
          STATES_FILL_LAYER,
          'fill-color',
          SELECT_FILL_COLOR
        );
        map.setPaintProperty(
          STATES_FILL_LAYER,
          'fill-opacity',
          STATE_FILL_OPACITY
        );
        map.setLayerZoomRange(STATES_FILL_LAYER, 0, 24);
      }
    };

    if (styleLoadedRef.current) apply();
    else map.once('style.load', apply);
  }, [heatMode, counties, countiesReady]);

  // Cross-highlight: outline the counties highlighted from the table.
  // biome-ignore lint/correctness/useExhaustiveDependencies: countiesReady re-applies the filter once the county layer exists
  useEffect(() => {
    const map = mapRef.current;
    if (!map?.getLayer(COUNTIES_HIGHLIGHT_LAYER)) return;
    map.setFilter(COUNTIES_HIGHLIGHT_LAYER, [
      'in',
      ['get', 'fips'],
      ['literal', highlightedFips ? [...highlightedFips] : []],
    ]);
  }, [highlightedFips, countiesReady]);

  // Highlight + fit to the current selection (or the impacted counties in
  // heat mode). Re-runs as the lazy county chunk lands.
  // biome-ignore lint/correctness/useExhaustiveDependencies: countiesReady re-applies once the chunk lands; helpers read refs
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // The default east-coast frame already sits past COUNTY_VISIBLE_ZOOM, so
    // load county geometry eagerly rather than waiting for a zoom/selection.
    ensureCounties();

    const apply = () => {
      if (!map.getLayer(STATES_SELECTED_LAYER)) return;
      // The selected output geo gets a green outline (in both explore and heat
      // mode) so it's clear which state/county is selected.
      map.setFilter(STATES_SELECTED_LAYER, [
        '==',
        ['get', 'fips'],
        selectedGeo?.level === 'state' ? selectedGeo.fips : '__none__',
      ]);
      if (map.getLayer(COUNTIES_SELECTED_LAYER)) {
        map.setFilter(COUNTIES_SELECTED_LAYER, [
          '==',
          ['get', 'fips'],
          selectedGeo?.level === 'county' ? selectedGeo.fips : '__none__',
        ]);
      }

      // Green-fill the selected geo on the explore page (first step of the
      // flow). In heat mode the fills carry the heat ramp, so we leave the
      // `selected` feature-state off there (the outline still marks it).
      if (selectedFeatureRef.current) {
        map.setFeatureState(selectedFeatureRef.current, { selected: false });
        selectedFeatureRef.current = null;
      }
      if (highlightSelectionRef.current && !heatMode && selectedGeo) {
        const source =
          selectedGeo.level === 'state' ? STATES_SOURCE : COUNTIES_SOURCE;
        const id = Number(selectedGeo.fips);
        if (Number.isFinite(id) && map.getSource(source)) {
          map.setFeatureState({ source, id }, { selected: true });
          selectedFeatureRef.current = { source, id };
        }
      }

      // Resolve the camera target + a signature of what it frames. Only
      // animate when the signature changed, so re-runs that don't change the
      // selection/impacted-set (e.g. the lazy county chunk landing) don't
      // fight the user's manual pan/zoom.
      let target: {
        bounds: mapboxgl.LngLatBoundsLike;
        padding: number;
      } | null = null;
      let signature: string;

      if (
        heatMode &&
        !fitToSelectedRef.current &&
        counties &&
        countiesGeoJsonRef.current
      ) {
        const acc: Bbox = [Infinity, Infinity, -Infinity, -Infinity];
        for (const fips of counties.keys()) {
          const feature = findFeature(countiesGeoJsonRef.current, fips);
          if (feature) accFeatureBbox(feature, acc);
        }
        if (isFiniteBbox(acc)) {
          target = { bounds: bboxToLngLat(acc), padding: 40 };
        }
        signature = `heat:${[...counties.keys()].sort().join(',')}`;
      } else if (!selectedGeo) {
        target = { bounds: EAST_COAST_BOUNDS, padding: 24 };
        signature = 'na';
      } else {
        const feature =
          selectedGeo.level === 'county'
            ? (countiesGeoJsonRef.current &&
                findFeature(countiesGeoJsonRef.current, selectedGeo.fips)) ||
              findFeature(usStatesGeoJson, selectedGeo.stateFips)
            : findFeature(usStatesGeoJson, selectedGeo.fips);
        if (feature) {
          target = {
            bounds: bboxToLngLat(bboxOfFeature(feature)),
            padding: 40,
          };
        }
        signature = `${selectedGeo.level}:${selectedGeo.fips}`;
      }

      if (target && signature !== lastFitSignatureRef.current) {
        lastFitSignatureRef.current = signature;
        map.fitBounds(target.bounds, {
          padding: target.padding,
          duration: 600,
        });
      }
    };

    if (styleLoadedRef.current) apply();
    else map.once('style.load', apply);
  }, [selectedGeo, heatMode, counties, countiesReady]);

  if (initError) {
    return (
      <div data-testid="economic-impact-map-error">
        Unable to load the map — WebGL is required.
      </div>
    );
  }

  const tooltipDatum =
    tooltip && heatMode ? counties?.get(tooltip.fips) : undefined;

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div
        ref={containerRef}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
        }}
        data-testid="economic-impact-map"
      />
      {tooltip && tooltipDatum && (
        <Portal>
          <MapCountyTooltip
            datum={tooltipDatum}
            clientX={tooltip.clientX}
            clientY={tooltip.clientY}
          />
        </Portal>
      )}
      {tooltip && heatMode && !tooltipDatum && (
        <Portal>
          <MapNameTooltip
            fips={tooltip.fips}
            name={tooltip.name}
            clientX={tooltip.clientX}
            clientY={tooltip.clientY}
          />
        </Portal>
      )}
      {tooltip && exploreMode && (
        <Portal>
          <MapExploreTooltip
            name={tooltip.name}
            fips={tooltip.fips}
            clientX={tooltip.clientX}
            clientY={tooltip.clientY}
          />
        </Portal>
      )}
    </div>
  );
}
